import { useState, useMemo } from "react";
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    isBefore,
    startOfDay,
    addDays,
    addWeeks
} from "date-fns";
import { ChevronLeft, ChevronRight, Plus, AlertCircle, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Board, Card } from "@/types/board";
import { EditCardDialog } from "./board/EditCardDialog";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { boardEvents, BOARD_UPDATED } from "@/lib/events";

interface CalendarViewProps {
    boards: Board[];
    onUpdate: () => void;
}

export function CalendarView({ boards, onUpdate }: CalendarViewProps) {
    const { toast } = useToast();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedCard, setSelectedCard] = useState<Card | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');

    // Add Task Dialog State
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newTaskTitle, setNewTaskTitle] = useState("");
    const [selectedBoardId, setSelectedBoardId] = useState("");
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    // Flatten all cards from all boards
    const allCards = useMemo(() => {
        const cards: (Card & { boardTitle: string; boardId: string; columnTitle: string })[] = [];
        boards.forEach((board) => {
            board.columns?.forEach((column) => {
                column.cards?.forEach((card) => {
                    cards.push({
                        ...card,
                        boardTitle: board.title,
                        boardId: board.id,
                        columnTitle: column.title,
                    });
                });
            });
        });
        return cards;
    }, [boards]);

    // Calendar Generation Logic - depends on view mode
    const calendarDays = useMemo(() => {
        if (viewMode === 'day') {
            return [currentDate];
        } else if (viewMode === 'week') {
            const weekStart = startOfWeek(currentDate);
            const weekEnd = endOfWeek(currentDate);
            return eachDayOfInterval({ start: weekStart, end: weekEnd });
        } else {
            // Month view
            const monthStart = startOfMonth(currentDate);
            const monthEnd = endOfMonth(monthStart);
            const startDate = startOfWeek(monthStart);
            const endDate = endOfWeek(monthEnd);
            return eachDayOfInterval({ start: startDate, end: endDate });
        }
    }, [currentDate, viewMode]);

    const isDone = (card: any) => {
        const status = card.columnTitle.toLowerCase();
        return status.includes('done') || status.includes('complete');
    };

    // Filter cards for specific dates
    const parseDate = (d: string) => new Date(d + 'T00:00:00');

    const getCardsForDate = (date: Date) => {
        return allCards.filter(card => {
            if (!card.due_date) return false;
            return isSameDay(parseDate(card.due_date), date) && !isDone(card);
        });
    };

    // Past Due Cards
    const pastDueCards = useMemo(() => {
        const today = startOfDay(new Date());
        return allCards.filter(card => {
            if (!card.due_date || isDone(card)) return false;
            return isBefore(parseDate(card.due_date), today);
        });
    }, [allCards]);

    const handleAddTask = async () => {
        if (!newTaskTitle.trim() || !selectedBoardId || !selectedDate) return;

        try {
            const board = boards.find((b) => b.id === selectedBoardId);
            if (!board || !board.columns || board.columns.length === 0) {
                toast({ title: "Error", description: "Board has no columns", variant: "destructive" });
                return;
            }

            const firstColumn = [...board.columns].sort((a, b) => a.order - b.order)[0];

            await api.post('/cards', {
                title:     newTaskTitle,
                column_id: firstColumn.id,
                due_date:  format(selectedDate, 'yyyy-MM-dd'),
                status:    'todo',
            });

            boardEvents.emit(BOARD_UPDATED);
            toast({ title: "Success", description: "Task added to calendar" });
            setNewTaskTitle("");
            setIsAddDialogOpen(false);
            onUpdate();
        } catch (error) {
            console.error("Error adding task:", error);
            toast({ title: "Error", description: "Failed to add task", variant: "destructive" });
        }
    };

    const handleMarkDone = async (card: typeof allCards[0]) => {
        try {
            const board = boards.find((b) => b.id === card.boardId);
            if (!board) return;

            const doneColumn = board.columns?.find((col) =>
                col.title.toLowerCase().includes('done') ||
                col.title.toLowerCase().includes('complete')
            );

            if (!doneColumn) {
                toast({ title: "Cannot mark as done", description: "No 'Done' column found in this board", variant: "destructive" });
                return;
            }

            if (card.column_id === doneColumn.id) return;

            await api.patch(`/cards/${card.id}`, { column_id: doneColumn.id, status: 'done' });

            boardEvents.emit(BOARD_UPDATED);
            toast({ title: "Task completed", description: "Moved to Done" });
            onUpdate();
        } catch (error) {
            console.error("Error updating task:", error);
            toast({ title: "Error", description: "Failed to update task", variant: "destructive" });
        }
    };

    const openAddDialog = (date: Date) => {
        setSelectedDate(date);
        setIsAddDialogOpen(true);
        // Default to first board if available
        if (boards.length > 0 && !selectedBoardId) {
            setSelectedBoardId(boards[0].id);
        }
    };

    return (
        <div className="flex h-[calc(100vh-200px)] gap-6 animate-in fade-in duration-500">
            {/* Main Calendar Area */}
            <div className="flex-1 flex flex-col bg-white/[0.02] border border-white/[0.05] rounded-xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/[0.05]">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                            <CalendarIcon className="h-5 w-5 text-indigo-400" />
                            {format(currentDate, viewMode === 'month' ? "MMMM yyyy" : viewMode === 'week' ? "'Week of' MMM d, yyyy" : "MMMM d, yyyy")}
                        </h2>
                        <div className="flex items-center gap-1 bg-white/[0.05] p-1 rounded-lg border border-white/[0.05]">
                            <Button
                                variant={viewMode === 'month' ? 'secondary' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode('month')}
                                className={viewMode === 'month' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'text-slate-400 hover:text-white'}
                            >
                                Month
                            </Button>
                            <Button
                                variant={viewMode === 'week' ? 'secondary' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode('week')}
                                className={viewMode === 'week' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'text-slate-400 hover:text-white'}
                            >
                                Week
                            </Button>
                            <Button
                                variant={viewMode === 'day' ? 'secondary' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode('day')}
                                className={viewMode === 'day' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'text-slate-400 hover:text-white'}
                            >
                                Day
                            </Button>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => {
                            if (viewMode === 'day') setCurrentDate(addDays(currentDate, -1));
                            else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, -1));
                            else setCurrentDate(subMonths(currentDate, 1));
                        }}>
                            <ChevronLeft className="h-4 w-4 text-slate-400" />
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => setCurrentDate(new Date())}
                            className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium shadow-lg shadow-indigo-500/20 border-0"
                        >
                            Today
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => {
                            if (viewMode === 'day') setCurrentDate(addDays(currentDate, 1));
                            else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, 1));
                            else setCurrentDate(addMonths(currentDate, 1));
                        }}>
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                        </Button>
                    </div>
                </div>

                {/* Weekday Headers */}
                <div className={`grid ${viewMode === 'day' ? 'grid-cols-1' : 'grid-cols-7'} border-b border-white/[0.05] bg-white/[0.01]`}>
                    {viewMode === 'day' ? (
                        <div className="p-2 text-center text-sm font-medium text-slate-400">
                            {format(currentDate, 'EEEE')}
                        </div>
                    ) : (
                        ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="p-2 text-center text-sm font-medium text-slate-400">
                                {day}
                            </div>
                        ))
                    )}
                </div>

                {/* Calendar Grid */}
                <div className="flex-1 overflow-y-auto">
                    <div className={`grid ${viewMode === 'day' ? 'grid-cols-1' : 'grid-cols-7'} auto-rows-fr min-h-full`}>
                        {calendarDays.map((day) => {
                            const dayCards = getCardsForDate(day);
                            const isToday = isSameDay(day, new Date());
                            const isCurrentMonth = isSameMonth(day, currentDate);

                            return (
                                <div
                                    key={day.toString()}
                                    className={`
                                    ${viewMode === 'month' ? 'min-h-[100px] max-h-[180px]' : 'min-h-[400px]'} border-b border-r border-white/[0.05] p-2 transition-colors hover:bg-white/[0.02] group relative flex flex-col
                                    ${!isCurrentMonth ? 'bg-black/20' : ''}
                                `}
                                    onClick={() => openAddDialog(day)}
                                >
                                    <div className="flex items-center justify-between mb-1 flex-shrink-0">
                                        <span className={`
                                        text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full
                                        ${isToday ? 'bg-indigo-500 text-white' : isCurrentMonth ? 'text-slate-300' : 'text-slate-600'}
                                    `}>
                                            {format(day, 'd')}
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 opacity-0 group-hover:opacity-100 -mr-1 text-slate-400 hover:text-white"
                                        >
                                            <Plus className="h-3 w-3" />
                                        </Button>
                                    </div>

                                    <div className="space-y-1 overflow-y-auto flex-1 pr-1 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                                        {dayCards.map(card => (
                                            <div
                                                key={card.id}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedCard(card);
                                                    setIsEditDialogOpen(true);
                                                }}
                                                className="px-2 py-1.5 rounded-md bg-white/[0.05] border border-white/[0.1] hover:bg-white/[0.08] transition-all cursor-pointer group/card flex flex-col gap-1 shadow-sm hover:shadow-md hover:border-indigo-500/30"
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <span className="text-xs font-medium text-slate-200 line-clamp-2 leading-tight">
                                                        {card.title}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-4 w-4 text-slate-400 hover:text-green-400 opacity-0 group-hover/card:opacity-100 transition-opacity -mt-0.5 -mr-1"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMarkDone(card);
                                                        }}
                                                        title="Mark as Done"
                                                    >
                                                        <div className="h-3 w-3 rounded-full border-2 border-current" />
                                                    </Button>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Badge variant="outline" className="text-[10px] h-4 px-1 py-0 border-white/[0.1] text-slate-400 bg-black/20">
                                                        {card.boardTitle}
                                                    </Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Side Panel: Past Due */}
            <div className="w-80 flex flex-col gap-4">
                <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl flex-1 flex flex-col overflow-hidden">
                    <div className="p-4 border-b border-white/[0.05] bg-red-500/5">
                        <h3 className="text-lg font-semibold text-red-400 flex items-center gap-2">
                            <AlertCircle className="h-5 w-5" />
                            Past Due ({pastDueCards.length})
                        </h3>
                    </div>
                    <ScrollArea className="flex-1 p-4">
                        <div className="space-y-3">
                            {pastDueCards.length === 0 ? (
                                <div className="text-center text-slate-500 py-8">
                                    No overdue tasks! 🎉
                                </div>
                            ) : (
                                pastDueCards.map(card => (
                                    <div
                                        key={card.id}
                                        onClick={() => {
                                            setSelectedCard(card);
                                            setIsEditDialogOpen(true);
                                        }}
                                        className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 transition-colors cursor-pointer group"
                                    >
                                        <div className="font-medium text-slate-200 mb-1">{card.title}</div>
                                        <div className="flex items-center justify-between text-xs text-slate-400">
                                            <span>{card.boardTitle}</span>
                                            <Badge variant="outline" className="border-red-500/30 text-red-400">
                                                {format(parseDate(card.due_date!), "MMM d")}
                                            </Badge>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </div>

            {/* Edit Card Dialog */}
            {selectedCard && (
                <EditCardDialog
                    card={selectedCard}
                    isOpen={isEditDialogOpen}
                    onOpenChange={setIsEditDialogOpen}
                    onUpdate={onUpdate}
                    boardTitle={(selectedCard as any).boardTitle}
                />
            )}

            {/* Add Task Dialog */}
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogContent className="bg-slate-900 border border-white/[0.05] text-white">
                    <DialogHeader>
                        <DialogTitle>Add Task for {selectedDate ? format(selectedDate, "MMM d, yyyy") : ""}</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="task-title">Task Title</Label>
                            <Input
                                id="task-title"
                                value={newTaskTitle}
                                onChange={(e) => setNewTaskTitle(e.target.value)}
                                placeholder="What needs to be done?"
                                className="bg-white/[0.02] border-white/[0.05] text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Project / Board</Label>
                            <Select value={selectedBoardId} onValueChange={setSelectedBoardId}>
                                <SelectTrigger className="bg-white/[0.02] border-white/[0.05] text-white">
                                    <SelectValue placeholder="Select a board" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-white/[0.05] text-white">
                                    {boards.map(board => (
                                        <SelectItem key={board.id} value={board.id}>{board.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIsAddDialogOpen(false)} className="hover:bg-white/[0.05] text-slate-400">Cancel</Button>
                        <Button onClick={handleAddTask} className="bg-indigo-500 hover:bg-indigo-600 text-white">Add Task</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
